import React, { Component } from 'react';
import ModalHeader from 'components/base/secure/modals/modal-header';
import MoneyField from 'components/form/money-field';
import CategoryPicker from 'components/pickers/category-picker';
import SaveButton from 'components/form/save-button';
import PropTypes from 'prop-types';
import ModalContent from 'components/base/secure/modals/modal-content';
import { withNamespaces } from 'react-i18next';
import { inject } from 'mobx-react';
import BudgetItemConflictModal from 'modules/budget/components/budget-modals/budget-item-conflict-modal';
import { SmallCategory } from 'store/budgetStore';

@withNamespaces()
@inject('store')
class EditItemModal extends Component {
    constructor(props) {
        super(props);

        const smallCategoryRef = props.smallCategoryRef;
        this.state = {
            loading: false,
            category: smallCategoryRef ? smallCategoryRef.id : props.category,
            amount: smallCategoryRef ? smallCategoryRef.budgeted : '',
            conflict: null,
        };

        this.dismiss = this.dismiss.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.raiseConflict = this.raiseConflict.bind(this);
        this.closeConflictDialog = this.closeConflictDialog.bind(this);
        this.resolveConflict = this.resolveConflict.bind(this);
        this.requestEdit = this.requestEdit.bind(this);
    }

    /** Submit the form */
    onSubmit(e) {
        e.preventDefault();

        const { loading, amount, category } = this.state;
        // TODO: Category validation

        if (!loading && category !== '' && category !== null) {
            this.setState({loading: true});

            const { smallCategoryRef, store, onSuccess } = this.props;
            const { budget } = store;

            // Check whether we are editing or creating a new item
            if (smallCategoryRef && smallCategoryRef.budgetItemId) {
                // We are editing this budget item
                this.requestEdit(smallCategoryRef);
            } else {
                // We are creating this budget item
                budget.requestAddBudgetItem({amount, category})
                    .then(() => onSuccess())
                    .catch(err => {
                        if (err.response && err.response.status === 409) {
                            // If there's a conflict, offer a way to resolve it
                            this.raiseConflict(err.response.data);
                        } else {
                            this.setState({loading: false})
                        }
                    });
            }
        }
    }

    /** Called when trying to create an already existing budget category */
    raiseConflict(conflictData) {
        this.setState({conflict: conflictData});
    }

    /** Called when user selects to cancel conflict resolve */
    closeConflictDialog() {
        this.setState({conflict: null, loading: false});
    }

    /** Called when user selects to resolve conflict in external modal */
    resolveConflict() {
        // Assign a SmallCategoryRef to this modal, since we opted to solve a conflict
        const category = this.state.conflict.category;
        const parentCategory = category.category || category;
        const bigCategory = this.props.store.budget.createOrFindBigCategory(parentCategory);
        const smallCategory = bigCategory.createOrFindSmallCategory(category);

        this.setState({conflict: null});
        this.requestEdit(smallCategory);
    }

    /** Send an edit request to store in case of edit modal or conflict resolution */
    requestEdit(smallCategoryRef) {
        const { onSuccess } = this.props;
        const { amount } = this.state;

        smallCategoryRef.requestEdit({amount})
            .then(() => onSuccess())
            .catch(() => this.setState({loading: false}));
    }

    /** Close this modal */
    dismiss() {
        if (!this.state.loading) {
            this.props.onDismiss()
        }
    }

    handleChange(e) {
        const { name, value } = e.target;

        this.setState({
            [name]: value,
        });
    }

    render() {
        const { t, smallCategoryRef } = this.props;
        const { loading, category, amount, conflict } = this.state;

        const conflictModal = conflict ? (
            <BudgetItemConflictModal
                conflict={conflict}
                newItem={{
                    amount: parseInt(amount, 10),
                    category,
                }}
                onDismiss={this.closeConflictDialog}
                onSuccess={this.resolveConflict}
            />
        ) : null;

        return (
            <ModalContent>
                <ModalHeader
                    title={t(smallCategoryRef && smallCategoryRef.budgetItemId ? 'BUDGET.BUDGET_ITEM.EDIT_TITLE' : 'BUDGET.BUDGET_ITEM.ADD_TITLE')}
                    close={this.dismiss}
                />
                <div className="modal-body">
                    <form onSubmit={this.onSubmit}>
                        <div style={{marginBottom: '20px'}}>
                            <CategoryPicker
                                name="category"
                                label="Category"
                                onChange={this.handleChange}
                                value={category}
                                required={true}
                                disabled={!!smallCategoryRef}
                            />
                        </div>
                        <div>
                            <MoneyField
                                name="amount"
                                label="Amount"
                                onChange={this.handleChange}
                                value={amount}
                                required={true}
                                focusOnRender={true}
                            />
                        </div>
                        <div className="modal-footer">
                            <SaveButton loading={loading} />
                            <button type="button"
                                    className="btn btn-link"
                                    onClick={this.dismiss}
                                    disabled={loading}
                            >
                                {t('COMMON.OR_CANCEL')}
                            </button>
                        </div>
                    </form>
                </div>
                {conflictModal}
            </ModalContent>
        );
    }
}

EditItemModal.propTypes = {
    /** A function that will be called when the modal will be successfully submitted */
    onSuccess: PropTypes.func.isRequired,

    /** A function that will be called when the modal is dismissed (like when user clicks outside) */
    onDismiss: PropTypes.func.isRequired,

    /** Category id to be set when modal is first opened, gets overridden by smallCategoryRef */
    category: PropTypes.number,

    /** SmallCategory from budget store if we're editing this item */
    smallCategoryRef: PropTypes.instanceOf(SmallCategory),
};

export default EditItemModal;


// WEBPACK FOOTER //
// ./src/modules/budget/components/budget-modals/edit-item-modal.js